import React from 'react';
import { motion } from 'framer-motion';

const TimelineItem = ({ year, title, desc, side, delay }) => (
  <motion.div
    initial={{ opacity: 0, x: side === 'left' ? -30 : 30 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8, delay }}
    className={`relative flex items-center justify-${side === 'left' ? 'end' : 'start'} md:w-1/2 ${side === 'left' ? 'pr-12 md:pr-16 text-right ml-0 mr-auto' : 'pl-12 md:pl-16 text-left ml-auto mr-0'} mb-24 md:mb-32`}
  >
    <div className={`absolute top-0 ${side === 'left' ? '-right-2' : '-left-2'} w-4 h-4 rounded-full bg-bronze-500 shadow-[0_0_15px_rgba(201,168,76,0.5)] z-10`} />
    <div className="max-w-md">
       <span className="text-2xl md:text-4xl font-serif italic text-bronze-400 mb-2 block">{year}</span>
       <h3 className="text-xl md:text-2xl font-serif text-theme mb-4">{title}</h3>
       <p className="text-xs md:text-sm text-theme-secondary leading-relaxed font-light">{desc}</p>
    </div>
  </motion.div>
);

const AboutUs = () => {
  return (
    <div className="bg-theme text-theme overflow-hidden pt-32">
      
      {/* ── HERO SECTION ── */}
      <section className="px-6 md:px-20 max-w-7xl mx-auto mb-32 md:mb-60">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <span className="text-[10px] font-mono uppercase tracking-ultra text-bronze-400 mb-6 block">Our Heritage</span>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif font-light leading-[1.1] mb-12">
            Engineering <br /> 
            <span className="italic text-bronze-gradient">Continuity.</span>
          </h1>
          <div className="grid lg:grid-cols-2 gap-16 md:gap-32 items-start">
             <p className="text-base md:text-xl text-theme-secondary leading-relaxed font-light italic">
               Founded in 1984 as ACS, our journey began with a singular focus on industrial electronics and engineering precision. Over four decades, we've evolved into IOTIQ — a unified force in full-stack automation.
             </p>
             <div className="aspect-[4/5] rounded-[3rem] overflow-hidden relative group shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1517420704952-d9f39e95b43e?auto=format&fit=crop&q=80&w=1500" 
                  alt="Vintage Engineering" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[3s]"
                />
                <div className="absolute inset-0 bg-bronze-900/10 mix-blend-overlay group-hover:opacity-0 transition-opacity" />
             </div>
          </div>
        </motion.div>
      </section>

      {/* ── BENTO GRID PHILOSOPHY ── */}
      <section className="px-6 md:px-20 py-24 md:py-40 bg-theme-secondary/20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
             <div className="md:col-span-2 p-10 md:p-16 rounded-[3.5rem] glass-surface-v2 border border-theme/10 bg-theme/40">
                <h2 className="text-3xl md:text-4xl font-serif font-light mb-8">Proprietary <br /><span className="italic text-bronze-400">Everything.</span></h2>
                <p className="text-theme-secondary leading-relaxed font-light max-w-xl">
                  We don't just assemble off-the-shelf components. From the initial chip architecture to the mobile app interface, IOTIQ controls every layer of the technology stack. This absolute ownership ensures unparalleled security, stability, and zero-latency performance.
                </p>
             </div>
             <div className="p-10 md:p-16 rounded-[3.5rem] glass-surface-v2 border border-theme/10 bg-bronze-gradient text-obsidian flex flex-col justify-between shadow-gold-glow-sm">
                <i className="fas fa-microchip text-4xl mb-12" />
                <h3 className="text-2xl font-serif italic">100% Native Architecture</h3>
             </div>
             <div className="p-10 md:p-16 rounded-[3.5rem] glass-surface-v2 border border-theme/10 bg-theme/40 flex flex-col justify-between">
                <i className="fas fa-handshake text-4xl text-bronze-400 mb-12" />
                <h3 className="text-2xl font-serif italic text-theme">Lifetime Partnership</h3>
             </div>
             <div className="md:col-span-2 p-10 md:p-16 rounded-[3.5rem] glass-surface-v2 border border-theme/10 relative overflow-hidden group">
                <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=1500" alt="Office" className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:scale-105 transition-transform duration-[3s]" />
                <div className="relative z-10">
                   <h2 className="text-3xl md:text-4xl font-serif font-light mb-8 text-theme">Hyderabad <br /><span className="italic text-bronze-400">Innovation Hub.</span></h2>
                   <p className="text-theme-secondary leading-relaxed font-light max-w-xl">
                     Our state-of-the-art facility in Hyderabad serves as our global R&D and manufacturing center, where 40 years of engineering legacy meets 2026 innovation.
                   </p>
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* ── TIMELINE ── */}
      <section className="py-32 md:py-60 px-6 max-w-7xl mx-auto relative">
        <div className="text-center mb-32 md:mb-48">
           <span className="text-[10px] font-mono uppercase tracking-ultra text-bronze-400 mb-6 block">Evolution Journey</span>
           <h2 className="text-4xl md:text-6xl font-serif font-light">The Timeline <br/><span className="italic text-bronze-gradient">Of Innovation.</span></h2>
        </div>

        {/* Vertical Line */}
        <div className="absolute left-1/2 -translate-x-1/2 top-[500px] bottom-60 w-px bg-gradient-to-b from-bronze-500/0 via-bronze-500/20 to-bronze-500/0 hidden md:block" />

        <div className="relative">
           <TimelineItem 
             side="left"
             year="1984"
             delay={0.1}
             title="Founding of ACS"
             desc="Started as an industrial electronics engineering firm focused on specialized controls and reliability."
           />
           <TimelineItem 
             side="right"
             year="1995"
             delay={0.2}
             title="Industrial Expansion"
             desc="Pioneered custom PLC solutions and SCADA integrations for major manufacturing hubs across India."
           />
           <TimelineItem 
             side="left"
             year="2012"
             delay={0.3}
             title="Enter Smart IoT"
             desc="Launched the first generation of native smart home hardware, integrating mobile control for the first time."
           />
           <TimelineItem 
             side="right"
             year="2020"
             delay={0.4}
             title="The IOTIQ Rebrand"
             desc="Unified industrial precision with consumer-centric design under the IOTIQ banner."
           />
           <TimelineItem 
             side="left"
             year="2026"
             delay={0.5}
             title="Next Horizons"
             desc="Deploying Neural-Hub 3.0 and expanding our experience centers globally, starting from Hyderabad."
           />
        </div>
      </section>

      {/* ── FINAL TEAM CALLOUT ── */}
      <section className="py-24 md:py-40 px-6 text-center border-t border-theme/10">
         <h2 className="text-3xl font-serif italic text-theme mb-8">Driven by Excellence.</h2>
         <p className="text-theme-secondary text-[10px] uppercase tracking-ultra font-mono mb-12">40+ Years of Manufacturing Leadership</p>
         <div className="h-20 w-px bg-gradient-to-b from-bronze-500 to-transparent mx-auto" />
      </section>

    </div>
  );
};

export default AboutUs;
